<template>
  <form
    id="mortgage-form"
    @submit.prevent
  >
    <input
      id="leadid_token"
      ref="leadDiv"
      name="universal_leadid"
      type="hidden"
      value=""
    >
    <div
      v-if="step === 'property-zip'"
      class="step"
    >
      <h2 class="step__title first__title">Start by entering your zip code:</h2>
      <div class="step__body">
        <input-zip
          :zip="formData.zipCode"
          @input="handleLocationData"
          @valid="handleInputValidity"
        />
        <next-step @nextStep="handleNextStep">Continue</next-step>
      </div>
    </div>
    <div
      v-else-if="step === 'property-type'"
      class="step"
    >
      <h2 class="step__title">What type of home do you have?</h2>
      <div class="step__body">
        <home-type
          v-model="formData.homeType"
          @input="nextStep"
        />
      </div>
    </div>
    <div
      v-else-if="step === 'property-use'"
      class="step"
    >
      <h2 class="step__title">How are you using this property?</h2>
      <div class="step__body">
        <home-usage
          v-model="formData.homeUsage"
          @input="nextStep"
        />
      </div>
    </div>
    <div
      v-else-if="step === 'property-value'"
      class="step"
    >
      <h2 class="step__title">Estimated Property Value</h2>
      <div class="step__body">
        <prop-val
          v-model="formData.propertyValue"
          :prop-val="formData.propertyValue?parseInt(formData.propertyValue):275000"
        />
        <next-step @nextStep="nextStep">Continue</next-step>
      </div>
    </div>
    <div
      v-else-if="step === 'mortgage-balance'"
      class="step"
    >
      <h2 class="step__title">Approximate Mortgage Balance</h2>
      <div class="step__body">
        <mort-val
          v-model="formData.mortgageBalance"
          :prop-val="parseInt(formData.propertyValue)"
          :mort-bal="parseInt(formData.mortgageBalance)"
        />
        <next-step @nextStep="handleNextStep">Continue</next-step>
      </div>
    </div>
    <div
      v-else-if="step === 'cashout'"
      class="step"
    >
      <h2 class="step__title">Approximate Cash Out
        <div class="step__subtitle">You may qualify to get up to <span style="color:green;">${{ parseInt((formData.propertyValue * 0.9) - formData.mortgageBalance).toLocaleString() }}</span> cash out from your home's equity</div>
      </h2>
      <div class="step__body">
        <cashout
          v-model="formData.cashout"
          :max-cashout="(parseInt(formData.propertyValue) * 0.9) - parseInt(formData.mortgageBalance)"
          :selected-cashout="parseInt(formData.cashout)"
        />
        <next-step @nextStep="nextStep">Continue</next-step>
      </div>
    </div>
    <div
      v-else-if="step === 'credit-rating'"
      class="step"
    >
      <h2 class="step__title">Estimate Your Credit Score:
        <div class="step__subtitle">Most homeowners have "Good" credit score</div>
      </h2>

      <div class="step__body">
        <credit-score
          v-model="formData.creditScore"
          @input="nextStep"
        />
      </div>
    </div>
    <div
      v-else-if="step === 'is-veteran'"
      class="step"
    >
      <h2 class="step__title">Have you or your spouse ever served in the military?</h2>
      <div class="step__body">
        <is-veteran
          v-model="formData.veteran"
          @input="nextStep"
        />
      </div>
    </div>
    <div
      v-else-if="step === 'home-address'"
      class="step"
    >
      <h2 class="step__title">Where do you currently live?
        <div class="step__subtitle">(NO mail will be sent. Only used for verification and matching)</div>
      </h2>

      <div class="step__body">
        <input-us-addr
          ref="InputUsAddress"
          v-model="formData.homeAddress"
          :street-address="formData.homeAddress"
          @valid="handleInputValidity"
          @update="handleUpdate"
        />
        <div class="input__hint">
          {{ formData.city }} {{ formData.state }}, {{ formData.zipCode }}
        </div>
        <next-step @nextStep="handleNextStep">Continue</next-step>
      </div>
    </div>
    <div
      v-else-if="step === 'personal-info'"
      class="step"
    >
      <h2 class="step__title">What is your name?
        <div class="step__subtitle">(your personal information is safe and secure)</div>
      </h2>

      <div class="step__body">
        <f-name
          ref="InputFirstName"
          v-model="formData.firstName"
          :first-name="formData.firstName"
          @valid="handleInputValidity"
        />
        <l-name
          ref="InputLastName"
          v-model="formData.lastName"
          :last-name="formData.lastName"
          @valid="handleInputValidity"
        />
        <next-step @nextStep="handleNextStep">Continue</next-step>
      </div>
    </div>
    <div
      v-else-if="step === 'ssn'"
      class="step"
    >
      <div class="ribbon ribbon--success">
        <div class="ribbon__title">Congratulations!</div>
        You're eligible for advanced lender matching.
      </div>
      <h2 class="step__title">Please enter the last 4 digits of your SSN</h2>
      <div class="step__body">
        <ssn
          ref="SSN"
          v-model="formData.ssn"
          :ssn-prop="formData.ssn"
          @valid="handleInputValidity"
        />
        <next-step @nextStep="handleNextStep">Continue</next-step>
        <button
          type="button"
          class="link link--small"
          @click.once="skipThisStep"
        >Skip this, I don’t want advanced matching</button>
      </div>
    </div>
    <div
      v-else-if="step === 'personal-contacts'"
      class="step"
    >
      <h2 class="step__title m25">Great news! Your results are ready to view
        <!--<div class="step__subtitle">Complete this final step</div>-->
      </h2>
      <div class="step__body">
        <input-email
          ref="InputEmail"
          v-model="formData.email"
          :email-prop="formData.email"
          @valid="handleInputValidity"
        />
        <input-usphone
          ref="InputUsPhone"
          v-model="formData.usPhone"
          :phone-prop="formData.usPhone"
          @valid="handleInputValidity"
        />
        <next-step @nextStep="handleNextStep">See my results</next-step>
        <p class="disclaimer">
          <input
            id="leadid_tcpa_disclosure"
            type="hidden"
          ><label for="leadid_tcpa_disclosure">By clicking See my results, you electronically agree to be matched with up to five <a
            href="https://www.guidetolenders.com/sharedIncludes/tcpaLendersPopup.html"
            target="_blank"
          >lenders</a> (potentially including Quicken Loans) and consent for them and GuidetoLenders to contact you by telephone (including by automated dialing systems and pre-recorded
          voice messages), mobile
          device (including SMS and MMS), and email, even if you are on a corporate, state or national Do Not Call list. Consent is not required in order to purchase goods and services from the lenders that reach out to you. You are also providing written consent under the Fair Credit Reporting Act for Guide to Lenders to obtain your credit score from our contracted credit bureau to match you with the lenders that will provide you with competing quotes and verify your identity.</label></p>

      </div>
    </div>
  </form>
</template>

<script>
import InputUsZip from './form-elements/InputUsZip'
import BtnNextStep from './form-elements/BtnNextStep'
import HomeTypes from './form-elements/HomeTypes'
import HomeUsage from './form-elements/HomeUsage'
import PropertyValue from './form-elements/PropertyValue'
import MortgageBalance from './form-elements/MortgageBalance'
import Cashout from './form-elements/Cashout'
import CreditScore from './form-elements/CreditScore'
import Veteran from './form-elements/Veteran'
import InputUsAddress from './form-elements/InputUsAddress'
import InputFirstName from './form-elements/InputFirstName'
import InputLastName from './form-elements/InputLastName'
import SSN from './form-elements/SSN'
import InputUsPhone from './form-elements/InputUsPhone'
import InputEmail from './form-elements/InputEmail'
import Loader from './form-elements/Loader'

export default {
  components: {
    'input-zip': InputUsZip,
    'next-step': BtnNextStep,
    'home-type': HomeTypes,
    'home-usage': HomeUsage,
    'prop-val': PropertyValue,
    'mort-val': MortgageBalance,
    'cashout': Cashout,
    'credit-score': CreditScore,
    'is-veteran': Veteran,
    'input-us-addr': InputUsAddress,
    'f-name': InputFirstName,
    'l-name': InputLastName,
    'ssn': SSN,
    'input-usphone': InputUsPhone,
    'input-email': InputEmail,
    'loader': Loader
  },
  props: {
    step: {
      type: String,
      default: 'property-zip'
    }
  },
  data () {
    return {
      formData: {},
      validInputs: {}
    }
  },
  computed: {
    skipToNext (val) {
      if ((this.formData.propertyValue * 0.9) - this.formData.mortgageBalance < 10000) {
        this.formData.cashout = 0
        this.$emit('skipToNext', this.formData)
        return false
      } else {
        return true
      }
    }
  },
  created () {
    this.getParams()
  },
  mounted () {
    let local = JSON.parse(localStorage.getItem('formInfo'))
    if (local) {
      this.formData = local
    }
  },
  methods: {
    handleUpdate (name, val) {
      this.formData.name = val
    },
    handleLocationData (zip, city, state) {
      this.formData.zipCode = zip
      this.formData.state = state
      this.formData.city = city
    },
    handleInputValidity (isValid, tagName) {
      this.validInputs[tagName] = isValid
    },
    handleNextStep () {
      switch (this.step) {
        case 'property-zip':
          if (this.validInputs['input-zip'] === true) {
            this.$emit('stepValid', this.formData)
          }
          break
        case 'mortgage-balance':
          if ((this.formData.propertyValue * 0.9) - this.formData.mortgageBalance < 10000) {
            this.formData.cashout = 0
            this.$emit('skipToNext', this.formData)
          } else {
            this.$emit('stepValid', this.formData)
          }
          break
        case 'home-address':
          if (this.validInputs['input-us-addr'] === true) {
            this.$emit('stepValid', this.formData)
          } else {
            this.$refs.InputUsAddress.triggerTouch()
          }
          break
        case 'ssn':
          if (this.validInputs['ssn'] === true) {
            this.$emit('stepValid', this.formData)
          } else {
            this.$refs.SSN.triggerTouch()
          }
          break
        case 'personal-info':
          if (this.validInputs['f-name'] === true && this.validInputs['l-name'] === true) {
            this.$emit('stepValid', this.formData)
          } else {
            this.$refs.InputFirstName.triggerTouch()
            this.$refs.InputLastName.triggerTouch()
          }
          break
        case 'personal-contacts':
          if (this.validInputs['input-usphone'] === true && this.validInputs['input-email'] === true) {
            this.$emit('stepValid', this.formData)
          } else {
            this.$refs.InputEmail.triggerTouch()
            if (this.validInputs['input-usphone'] !== true) {
              this.$refs.InputUsPhone.triggerTouch()
            }
          }
          break
      }
    },
    nextStep () {
      if (this.$refs.leadDiv.value === '') {
        if (window.LeadiD.token !== '') {
          this.$refs.leadDiv.value = window.LeadiD.token
        }
      }
      this.formData.leadID = this.$refs.leadDiv.value
      this.$emit('stepValid', this.formData)
    },
    skipThisStep () {
      switch (this.step) {
        case 'ssn':
          this.formData.ssn = ''
          break
      }
      this.$emit('stepValid', this.formData)
    },
    getParams () {
      this.formData = this.$route.query
    }
  }
}
</script>

<style>

</style>
